import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Divider, IconButton, Menu, MenuItem } from '@material-ui/core'
import { MoreHorizontal } from 'react-feather'
import {
  ProjectFragment,
  ProjectVersionFragment,
} from '../../generated/graphql'
import { useProjectVersions } from '../../project-versions/use-project-versions'
import {
  FormValues as VersionFormValues,
  VersionDialog,
} from '../project/VersionDialog'
import { useNotifications } from '../../notifications/use-notifications'
import { useAlert } from '../alert/AlertProvider'
import {
  FormValues as ProjectFormValues,
  ProjectDialog,
  NO_BASE_VERSION,
} from '../project/ProjectDialog'
import { useProjects } from '../../project/use-projects'
import { useAuthorization } from '../../authorization/use-authorization'
import { ExportDialog } from '../project/ExportDialog'

type ProjectVersionsParams = {
  project: ProjectFragment
  activeVersion: ProjectVersionFragment
  onVersionSelected: (version: ProjectVersionFragment) => void
}

export function ProjectVersionsButton({
  project,
  activeVersion,
  onVersionSelected,
}: ProjectVersionsParams): React.ReactElement {
  const { t } = useTranslation()
  const { enqueueInfo, enqueueSuccess, enqueueError, closeSnackbar } =
    useNotifications()
  const history = useHistory()
  const { alert } = useAlert()
  const { updateProject, deleteProject } = useProjects()
  const { createProjectVersion, updateProjectVersion, removeProjectVersion } =
    useProjectVersions()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false)
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const canEditProject = useAuthorization().canEditProject(project)

  const onEditProject = async ({
    name,
    projectId,
    versionName,
    calendarYear,
    baseVersionId,
  }: ProjectFormValues) => {
    const base = baseVersionId === NO_BASE_VERSION ? null : baseVersionId

    await Promise.all([
      updateProject(project.id, {
        name,
        projectId,
        calendarYear: Number(calendarYear),
      }),

      updateProjectVersion(activeVersion.id, {
        name: versionName,
        baseVersionId: base,
      }),
    ])

    setIsProjectDialogOpen(false)
  }

  const onDeleteProject = () => {
    setMenuAnchorEl(null)

    alert(
      t('project.deleteDialog.title'),
      t('project.deleteDialog.message', { name: project.name }),
      [
        {
          title: t('common.cancel'),
        },
        {
          title: t('common.remove'),
          color: 'secondary',
          onPress: async () => {
            try {
              await deleteProject(project.id)
              enqueueSuccess(t('project.deleteDialog.success'))
              history.push('/')
            } catch (e) {
              enqueueError(t('project.deleteDialog.failed'))
            }
          },
        },
      ],
    )
  }

  const onCreateVersion = async ({
    name,
    useActiveAsBase,
    useBaseVersion,
  }: VersionFormValues) => {
    const infoKey = enqueueInfo(t('projectVersions.createInfo'))

    try {
      const maybeActiveVersionId = useActiveAsBase
        ? activeVersion.id
        : undefined
      const newVersion = await createProjectVersion(
        project,
        name,
        maybeActiveVersionId,
        !useBaseVersion,
      )
      onVersionSelected(newVersion)

      enqueueSuccess(t('projectVersions.createSuccess'))
      setIsVersionDialogOpen(false)
    } catch (e) {
      enqueueError(t('projectVersions.createError'))
    }

    closeSnackbar(infoKey)
  }

  const onDeleteVersion = () => {
    setMenuAnchorEl(null)
    alert(
      t('projectVersions.removeDialog.title'),
      t('projectVersions.removeDialog.message', { ...activeVersion }),
      [
        {
          title: t('common.cancel'),
        },
        {
          title: t('common.remove'),
          color: 'secondary',
          onPress: async () => {
            const infoKey = enqueueInfo(t('projectVersions.removeInfo'))

            try {
              await removeProjectVersion(activeVersion.id, project)
              enqueueSuccess(t('projectVersions.removeSuccess'))
            } catch (e) {
              enqueueError(t('projectVersions.removeError'))
            }

            closeSnackbar(infoKey)
          },
        },
      ],
    )
  }

  const baseVersionOptions = project.versions
    .filter((version) => version.id !== activeVersion.id)
    .map((version) => ({ id: version.id, name: version.name }))

  return (
    <>
      <IconButton
        id="open-version-menu-button"
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        <MoreHorizontal />
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            setIsProjectDialogOpen(true)
          }}
          disabled={!canEditProject}
        >
          {t('project.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            setIsExportDialogOpen(true)
          }}
        >
          {t('project.export')}
        </MenuItem>
        <MenuItem
          id="create-version"
          onClick={() => {
            setMenuAnchorEl(null)
            setIsVersionDialogOpen(true)
          }}
          disabled={!canEditProject}
        >
          {t('projectVersions.createButton')}
        </MenuItem>
        <MenuItem
          onClick={onDeleteVersion}
          disabled={!canEditProject || activeVersion.isDefault}
        >
          {t('projectVersions.remove')}
        </MenuItem>

        <Divider />

        <MenuItem
          id="delete-project"
          onClick={onDeleteProject}
          disabled={!canEditProject}
        >
          {t('project.delete')}
        </MenuItem>
      </Menu>

      <ProjectDialog
        defaultValues={{
          name: project.name,
          projectId: project.projectId,
          versionName: activeVersion.name,
          baseVersionId: activeVersion.baseVersionId ?? NO_BASE_VERSION,
          calendarYear: String(project.calendarYear),
        }}
        baseVersionOptions={baseVersionOptions}
        canEditBaseVersion={!activeVersion.isDefault}
        isOpen={isProjectDialogOpen}
        onSubmit={onEditProject}
        onClose={() => setIsProjectDialogOpen(false)}
      />

      <VersionDialog
        isOpen={isVersionDialogOpen}
        activeVersion={activeVersion}
        onSubmit={onCreateVersion}
        onClose={() => setIsVersionDialogOpen(false)}
      />

      <ExportDialog
        isOpen={isExportDialogOpen}
        activeVersion={activeVersion}
        onClose={() => setIsExportDialogOpen(false)}
      />
    </>
  )
}
